import { useCallback, useMemo, useRef } from 'react';
import {
  PROFILE_STORES_APPOINTMENTS_BOOKING_STATUS,
  PROFILE_STORES_APPOINTMENTS_BOOKING_DETAILS,
  PROFILE_STORES_APPOINTMENTS_BOOKING_ARRIVAL_OTP,
  PROFILE_STORES_APPOINTMENTS_BOOKING_ARRIVAL_OTP_VERIFY,
  PROFILE_STORES_APPOINTMENTS_BOOKING_WALKIN_DELETE,
  PROFILE_STORES_APPOINTMENTS_BOOKING_WALKIN_EDIT,
  PROFILE_STORES_APPOINTMENTS_BOOKINGS,
  PROFILE_STORES_APPOINTMENTS_BOOKINGS_BASE,
  PROFILE_STORES_APPOINTMENTS_BOOKINGS_RANGE,
  PROFILE_STORES_APPOINTMENTS_HOLD,
  PROFILE_STORES_APPOINTMENTS_HOLDS,
  PROFILE_STORES_APPOINTMENTS_SERVICE,
  PROFILE_STORES_APPOINTMENTS_SERVICE_DAY_HOLD,
  PROFILE_STORES_APPOINTMENTS_SERVICE_HOLDS,
  PROFILE_STORES_APPOINTMENTS_SERVICE_SLOT_SUMMARIES,
  PROFILE_STORES_APPOINTMENTS_SERVICES,
  PROFILE_STORES_APPOINTMENTS_SETTINGS,
  PROFILE_STORES_APPOINTMENTS_SLOTS,
  PROFILE_STORES_APPOINTMENTS_SLOTS_DELETE
} from '../../../shared/constants/endpoints';
import useApiRequest from '../../../shared/hooks/useApiRequest';
import { toBackendBookingStatus } from '../utils/bookingStatus';

const useAppointments = ({ request, storeToken, onToast }) => {
  const {
    execute,
    loading,
    error
  } = useApiRequest();

  const onToastRef = useRef(onToast);
  onToastRef.current = onToast;

  const ensureRequest = useCallback(async (executor, logModule, fallbackMessage) => {
    if (typeof request !== 'function' || !storeToken) {
      return null;
    }

    return execute(executor, {
      logModule,
      onError: (errorValue) => {
        const message = errorValue?.data?.message || errorValue?.message || fallbackMessage;
        onToastRef.current?.({ message, variant: 'error' });
      }
    });
  }, [execute, request, storeToken]);

  const fetchSettings = useCallback(async () => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_SETTINGS, {
      method: 'GET',
      requestName: 'appointments-settings-get'
    }), 'useAppointments.fetchSettings', 'Unable to load appointment settings.');
  }, [ensureRequest, request]);

  const fetchServices = useCallback(async () => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_SERVICES, {
      method: 'GET',
      requestName: 'appointments-services-get'
    }), 'useAppointments.fetchServices', 'Unable to load services.');
  }, [ensureRequest, request]);

  const fetchBookings = useCallback(async (windowKey, dateString = null) => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_BOOKINGS(windowKey, dateString), {
      method: 'GET',
      requestName: `appointments-bookings-${dateString || windowKey}`
    }), 'useAppointments.fetchBookings', 'Unable to load bookings.');
  }, [ensureRequest, request]);

  const fetchBookingsRange = useCallback(async (startDate, endDate) => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_BOOKINGS_RANGE(startDate, endDate), {
      method: 'GET',
      requestName: `appointments-bookings-range-${startDate}-${endDate}`
    }), 'useAppointments.fetchBookingsRange', 'Unable to load appointment history.');
  }, [ensureRequest, request]);

  const fetchBookingDetails = useCallback(async (bookingUuid) => {
    if (!bookingUuid) {
      return null;
    }
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_BOOKING_DETAILS(bookingUuid), {
      method: 'GET',
      requestName: `appointments-booking-details-${bookingUuid}`
    }), 'useAppointments.fetchBookingDetails', 'Unable to load appointment details.');
  }, [ensureRequest, request]);

  const fetchSlots = useCallback(async (serviceUuid, dateKey) => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_SLOTS(serviceUuid, dateKey), {
      method: 'GET',
      requestName: `appointments-slots-${serviceUuid}-${dateKey}`
    }), 'useAppointments.fetchSlots', 'Unable to load slots.');
  }, [ensureRequest, request]);

  const fetchServiceSlotSummaries = useCallback(async (dateKey) => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_SERVICE_SLOT_SUMMARIES(dateKey), {
      method: 'GET',
      requestName: `appointments-service-slot-summaries-${dateKey || 'today'}`
    }), 'useAppointments.fetchServiceSlotSummaries', 'Unable to load service slot summaries.');
  }, [ensureRequest, request]);

  const saveSettings = useCallback(async (payload) => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_SETTINGS, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      requestName: 'appointments-settings-save'
    }), 'useAppointments.saveSettings', 'Unable to save settings.');
  }, [ensureRequest, request]);

  const updateService = useCallback(async (serviceUuid, payload) => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_SERVICE(serviceUuid), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      requestName: 'appointments-service-update'
    }), 'useAppointments.updateService', 'Unable to update service.');
  }, [ensureRequest, request]);

  const createService = useCallback(async (payload) => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_SERVICES, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      requestName: 'appointments-service-create'
    }), 'useAppointments.createService', 'Unable to save service.');
  }, [ensureRequest, request]);

  const deleteService = useCallback(async (serviceUuid) => {
    if (!serviceUuid) {
      return null;
    }
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_SERVICE(serviceUuid), {
      method: 'DELETE',
      requestName: 'appointments-service-delete'
    }), 'useAppointments.deleteService', 'Unable to delete service.');
  }, [ensureRequest, request]);

  const updateBookingStatus = useCallback(async (bookingUuid, newStatus, cancellationReason = null) => {
    const backendStatus = toBackendBookingStatus(newStatus);

    const payload = { status: backendStatus };
    if (cancellationReason) {
      payload.cancellationReason = cancellationReason;
    }

    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_BOOKING_STATUS(bookingUuid), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      requestName: 'appointments-booking-status-update'
    }), 'useAppointments.updateBookingStatus', 'Unable to update booking status.');
  }, [ensureRequest, request]);

  const issueArrivalOtp = useCallback(async (bookingUuid) => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_BOOKING_ARRIVAL_OTP(bookingUuid), {
      method: 'POST',
      requestName: 'appointments-booking-arrival-otp-issue'
    }), 'useAppointments.issueArrivalOtp', 'Unable to send arrival OTP.');
  }, [ensureRequest, request]);

  const verifyArrivalOtp = useCallback(async (bookingUuid, otp) => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_BOOKING_ARRIVAL_OTP_VERIFY(bookingUuid), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ otp }),
      requestName: 'appointments-booking-arrival-otp-verify'
    }), 'useAppointments.verifyArrivalOtp', 'Unable to verify arrival OTP.');
  }, [ensureRequest, request]);

  // F09: Create booking (supports both regular and walk-in bookings)
  const createBooking = useCallback(async (payload) => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_BOOKINGS_BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      requestName: 'appointments-booking-create'
    }), 'useAppointments.createBooking', 'Unable to create booking.');
  }, [ensureRequest, request]);

  // Schedule Holds API (B03 fix - replacing localStorage)
  const fetchHoldsForService = useCallback(async (serviceUuid) => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_SERVICE_HOLDS(serviceUuid), {
      method: 'GET',
      requestName: 'appointments-service-holds-get'
    }), 'useAppointments.fetchHoldsForService', 'Unable to load schedule holds.');
  }, [ensureRequest, request]);

  const fetchAllHolds = useCallback(async () => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_HOLDS, {
      method: 'GET',
      requestName: 'appointments-holds-get-all'
    }), 'useAppointments.fetchAllHolds', 'Unable to load schedule holds.');
  }, [ensureRequest, request]);

  const createHold = useCallback(async (serviceUuid, payload) => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_SERVICE_HOLDS(serviceUuid), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      requestName: 'appointments-hold-create'
    }), 'useAppointments.createHold', 'Unable to create schedule hold.');
  }, [ensureRequest, request]);

  const createDayHold = useCallback(async (serviceUuid, payload) => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_SERVICE_DAY_HOLD(serviceUuid), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      requestName: 'appointments-day-hold-create'
    }), 'useAppointments.createDayHold', 'Unable to create schedule hold.');
  }, [ensureRequest, request]);

  const updateHold = useCallback(async (holdUuid, payload) => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_HOLD(holdUuid), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      requestName: 'appointments-hold-update'
    }), 'useAppointments.updateHold', 'Unable to update schedule hold.');
  }, [ensureRequest, request]);

  const deleteHold = useCallback(async (holdUuid) => {
    return ensureRequest(() => request(PROFILE_STORES_APPOINTMENTS_HOLD(holdUuid), {
      method: 'DELETE',
      requestName: 'appointments-hold-delete'
    }), 'useAppointments.deleteHold', 'Unable to delete schedule hold.');
  }, [ensureRequest, request]);

  // F42: Delete slot with cascading cancellation
  const deleteSlot = useCallback(async (serviceUuid, date, startTime, endTime, cancellationReason) => {
    return ensureRequest(() => request(
      PROFILE_STORES_APPOINTMENTS_SLOTS_DELETE(serviceUuid, date, startTime, endTime),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cancellationReason }),
        requestName: 'appointments-slot-delete'
      }
    ), 'useAppointments.deleteSlot', 'Unable to delete slot.');
  }, [ensureRequest, request]);

  // F45: Edit walk-in booking
  const editWalkinBooking = useCallback(async (bookingUuid, payload) => {
    return ensureRequest(() => request(
      PROFILE_STORES_APPOINTMENTS_BOOKING_WALKIN_EDIT(bookingUuid),
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        requestName: 'appointments-edit-walkin-booking'
      }
    ), 'useAppointments.editWalkinBooking', 'Unable to edit walk-in booking.');
  }, [ensureRequest, request]);

  // F45: Delete walk-in booking
  const deleteWalkinBooking = useCallback(async (bookingUuid) => {
    return ensureRequest(() => request(
      PROFILE_STORES_APPOINTMENTS_BOOKING_WALKIN_DELETE(bookingUuid),
      {
        method: 'DELETE',
        requestName: 'appointments-delete-walkin-booking'
      }
    ), 'useAppointments.deleteWalkinBooking', 'Unable to delete walk-in booking.');
  }, [ensureRequest, request]);

  return useMemo(() => ({
    fetchSettings,
    fetchServices,
    fetchBookings,
    fetchBookingsRange,
    fetchBookingDetails,
    fetchSlots,
    fetchServiceSlotSummaries,
    saveSettings,
    updateService,
    createService,
    deleteService,
    updateBookingStatus,
    issueArrivalOtp,
    verifyArrivalOtp,
    createBooking,
    fetchHoldsForService,
    fetchAllHolds,
    createHold,
    createDayHold,
    updateHold,
    deleteHold,
    deleteSlot,
    editWalkinBooking,
    deleteWalkinBooking,
    loading,
    error
  }), [
    createBooking,
    createHold,
    createDayHold,
    createService,
    deleteHold,
    deleteService,
    deleteSlot,
    deleteWalkinBooking,
    editWalkinBooking,
    error,
    fetchAllHolds,
    fetchBookings,
    fetchBookingsRange,
    fetchBookingDetails,
    fetchHoldsForService,
    fetchServices,
    fetchSettings,
    fetchServiceSlotSummaries,
    fetchSlots,
    loading,
    saveSettings,
    issueArrivalOtp,
    updateBookingStatus,
    updateHold,
    updateService,
    verifyArrivalOtp
  ]);
};

export default useAppointments;
